import { motion } from "framer-motion";
import { ArrowUpRight, BatteryCharging, Cable, Camera, Cpu, Droplets } from "lucide-react";

const services = [
  {
    num: "01",
    title: "Écran cassé",
    slug: "ecran",
    icon: "ecran",
    desc: "Vitre et dalle remplacées, tactile et couleurs d'origine, joint d'étanchéité refait au remontage.",
    img: "https://images.pexels.com/photos/288479/pexels-photo-288479.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    num: "02",
    title: "Batterie",
    slug: "batterie",
    icon: "batterie",
    desc: "Autonomie en chute libre, batterie gonflée, coupures intempestives.",
    img: "https://images.pexels.com/photos/6755075/pexels-photo-6755075.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    num: "03",
    title: "Connecteur de charge",
    slug: "connecteur-charge",
    icon: "cable",
    desc: "Faux contacts, câble qui ne tient plus : nettoyage d'abord, remplacement de la nappe si besoin.",
    img: "https://images.pexels.com/photos/37498139/pexels-photo-37498139.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    num: "04",
    title: "Caméra",
    slug: "camera",
    icon: "camera",
    desc: "Optique rayée, photos floues, Face ID / capteurs défaillants.",
    img: "https://images.pexels.com/photos/6755075/pexels-photo-6755075.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    num: "05",
    title: "Désoxydation",
    slug: "desoxydation",
    icon: "eau",
    desc: "Chute dans l'eau, oxydation, corrosion des composants.",
    img: "https://images.pexels.com/photos/37498139/pexels-photo-37498139.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    num: "06",
    title: "Logiciel",
    slug: "logiciel",
    icon: "logiciel",
    desc: "Blocages, sauvegarde, transfert de données, remise à zéro.",
    img: "https://images.pexels.com/photos/288479/pexels-photo-288479.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

export const Services = () => {
  return (
    <section id="services" className="relative py-28 lg:py-40" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.h2
          className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight uppercase text-zinc-950 mb-20"
        >
          Ce que nous <span className="text-chrome">réparons</span>
        </motion.h2>
        <motion.div
          className="mb-20 -mt-12 max-w-2xl"
          data-testid="services-intro"
        >
          <p className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#0055FF]">
            Toutes les pannes, toutes les marques.
          </p>
          <p className="mt-3 text-base md:text-lg font-light leading-relaxed text-zinc-600">
            iPhone, Samsung, Xiaomi, Google Pixel... Diagnostic et devis
            gratuits avant toute intervention.
          </p>
        </motion.div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((service, i) => (
            <motion.a
              key={service.num}
              href={`/reparation-${service.slug}-lyon`}
              className="group relative border border-zinc-200 bg-white p-8 hover:border-[#0055FF]/60 hover:bg-[#0055FF]/5 transition-colors duration-500 cursor-pointer block"
              data-testid={`service-item-${service.num}`}
              aria-label={`Voir la page ${service.title}`}
            >
              <div className="flex items-start justify-between mb-10">
                <div className="flex items-center gap-3">
                  <span className="font-display text-sm font-medium text-zinc-600 group-hover:text-[#0055FF] transition-colors duration-300">
                    {service.num}
                  </span>
                  {service.icon === "ecran" && (
                    <svg
                      viewBox="0 0 24 24"
                      className="h-7 w-7 text-zinc-600 group-hover:text-[#0055FF] transition-colors duration-300"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      data-testid="service-icon-ecran-casse"
                      aria-label="Icône écran cassé"
                    >
                      <rect x="6" y="2.5" width="12" height="19" rx="2.5" />
                      <polyline points="10,5.5 12.5,9 10.5,12 13.5,15.5" />
                      <line x1="10.5" y1="18.5" x2="13.5" y2="18.5" />
                    </svg>
                  )}
                  {service.icon === "cable" && (
                    <Cable
                      className="h-7 w-7 text-zinc-600 group-hover:text-[#0055FF] transition-colors duration-300"
                      strokeWidth={1.5}
                      data-testid="service-icon-connecteur"
                      aria-label="Icône connecteur de charge"
                    />
                  )}
                  {service.icon === "batterie" && (
                    <BatteryCharging
                      className="h-7 w-7 text-zinc-600 group-hover:text-[#0055FF] transition-colors duration-300"
                      strokeWidth={1.5}
                      data-testid="service-icon-batterie"
                      aria-label="Icône batterie"
                    />
                  )}
                  {service.icon === "camera" && (
                    <Camera
                      className="h-7 w-7 text-zinc-600 group-hover:text-[#0055FF] transition-colors duration-300"
                      strokeWidth={1.5}
                      data-testid="service-icon-camera"
                      aria-label="Icône caméra"
                    />
                  )}
                  {service.icon === "eau" && (
                    <Droplets
                      className="h-7 w-7 text-zinc-600 group-hover:text-[#0055FF] transition-colors duration-300"
                      strokeWidth={1.5}
                      data-testid="service-icon-desoxydation"
                      aria-label="Icône désoxydation"
                    />
                  )}
                  {service.icon === "logiciel" && (
                    <Cpu
                      className="h-7 w-7 text-zinc-600 group-hover:text-[#0055FF] transition-colors duration-300"
                      strokeWidth={1.5}
                      data-testid="service-icon-logiciel"
                      aria-label="Icône logiciel"
                    />
                  )}
                </div>
                <ArrowUpRight className="h-5 w-5 text-zinc-700 group-hover:text-[#0055FF] group-hover:rotate-45 transition-all duration-500" />
              </div>
              <h3 className="font-display text-2xl lg:text-3xl font-bold uppercase tracking-tight text-zinc-900 mb-4">
                <span className="group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-zinc-500 group-hover:via-zinc-800 group-hover:to-zinc-950">
                  {service.title}
                </span>
              </h3>
              <p className="text-sm font-light leading-relaxed text-zinc-500 group-hover:text-zinc-300 transition-colors duration-300">
                {service.desc}
              </p>
            </motion.a>
          ))}
        </div>

        <motion.p
          className="mt-12 text-sm font-light text-zinc-500 max-w-2xl"
        >
          Chaque réparation est réalisée avec des pièces premium testées et
          couverte par une garantie de 3 mois.
        </motion.p>
      </div>
    </section>
  );
};
